import type {
  AgentProgress,
  UIBlock,
  ScopeBlock,
  ToolCallBlock,
  MessageBlock,
  ErrorBlock,
  ThinkingBlock,
  ThoughtStreamBlock,
  TaskBlock,
} from '@trinity/chat';
import type {
  TimelineNode,
  ScopeNode,
  ToolCallNode,
  MessageNode,
  ErrorNode,
  ReasoningNode,
  TaskNode,
  CardPhase,
  CardState,
  ParallelGroupNode,
} from './interfaces';

function isScopeBlock(block: UIBlock): block is ScopeBlock {
  return block.kind === 'scope';
}

function isToolCallBlock(block: UIBlock): block is ToolCallBlock {
  return block.kind === 'tool_call';
}

function isMessageBlock(block: UIBlock): block is MessageBlock {
  return block.kind === 'message';
}

function isErrorBlock(block: UIBlock): block is ErrorBlock {
  return block.kind === 'error';
}

function isThinkingBlock(block: UIBlock): block is ThinkingBlock {
  return block.kind === 'thinking';
}

function isThoughtStreamBlock(block: UIBlock): block is ThoughtStreamBlock {
  return block.kind === 'thought_stream';
}

function isTaskBlock(block: UIBlock): block is TaskBlock {
  return block.kind === 'task';
}

export function getTimelineNodeKey(node: TimelineNode): string {
  switch (node.kind) {
    case 'reasoning':
      return node.id;
    case 'parallel_group':
      return `pg_${node.children.map(getTimelineNodeKey).join('_')}`;
    case 'scope':
    case 'tool_call':
    case 'message':
    case 'error':
    case 'task':
      return node.block.id;
  }
}

function shouldSkipAsStandalone(state: AgentProgress, block: UIBlock): boolean {
  if (block.kind !== 'thought_stream') return false;

  const parent = block.parentBlockId ? state.blocks[block.parentBlockId] : undefined;
  return !!parent && parent.kind === 'thinking';
}

function buildReasoningNode(state: AgentProgress, thinkingBlock: ThinkingBlock): ReasoningNode {
  const thoughtChild = thinkingBlock.childBlockIds
    .map((id) => state.blocks[id])
    .find((child): child is ThoughtStreamBlock => !!child && isThoughtStreamBlock(child));

  const status = thoughtChild?.status ?? thinkingBlock.status;
  const durationMs = status === 'completed' ? thinkingBlock.updatedAt - thinkingBlock.createdAt : undefined;

  return {
    kind: 'reasoning',
    id: thinkingBlock.id,
    status,
    summary: thinkingBlock.summary,
    detailText: thoughtChild?.text,
    streaming: thoughtChild?.streaming ?? thinkingBlock.status === 'running',
    depth: thinkingBlock.depth,
    durationMs,
  };
}

export function buildTimelineNode(state: AgentProgress, blockId: string): TimelineNode | null {
  const block = state.blocks[blockId];
  if (!block) return null;

  if (shouldSkipAsStandalone(state, block)) {
    return null;
  }

  if (isThinkingBlock(block)) {
    return buildReasoningNode(state, block);
  }

  if (isScopeBlock(block)) {
    const children = block.childBlockIds
      .map((childId) => buildTimelineNode(state, childId))
      .filter((node): node is TimelineNode => node !== null);

    const node: ScopeNode = {
      kind: 'scope',
      block,
      children,
    };

    return node;
  }

  if (isToolCallBlock(block)) {
    const children = block.childBlockIds
      .map((childId) => buildTimelineNode(state, childId))
      .filter((node): node is TimelineNode => node !== null);

    const node: ToolCallNode = {
      kind: 'tool_call',
      block,
      children,
    };

    return node;
  }

  if (isMessageBlock(block)) {
    const node: MessageNode = {
      kind: 'message',
      block,
    };

    return node;
  }

  if (isErrorBlock(block)) {
    const node: ErrorNode = {
      kind: 'error',
      block,
    };

    return node;
  }

  if (isTaskBlock(block)) {
    const node: TaskNode = {
      kind: 'task',
      block,
    };

    return node;
  }

  // standalone thought_stream fallback if not parented to thinking
  if (block.kind === 'thought_stream') {
    return {
      kind: 'reasoning',
      id: block.id,
      status: block.status,
      summary: 'Reasoning',
      detailText: block.text,
      streaming: block.streaming,
      depth: block.depth,
    };
  }

  return null;
}

export function buildTimeline(state: AgentProgress): TimelineNode[] {
  return state.rootTimeline
    .map((blockId) => buildTimelineNode(state, blockId))
    .filter((node): node is TimelineNode => node !== null);
}

const PARALLEL_THRESHOLD_MS = 500;

/** Flatten scope nodes so their children render directly in the parent list. */
export function flattenNodes(nodes: TimelineNode[]): TimelineNode[] {
  const result: TimelineNode[] = [];
  for (const node of nodes) {
    if (node.kind === 'scope') {
      result.push(...flattenNodes(node.children));
    } else {
      result.push(node);
    }
  }
  return result;
}

/** Attach the preceding reasoning's detail text to each tool call node. */
export function attachReasoningToToolCalls(nodes: TimelineNode[]): TimelineNode[] {
  let lastReasoningText: string | undefined;

  return nodes.map((node) => {
    if (node.kind === 'reasoning') {
      lastReasoningText = node.detailText;
      return node;
    }

    if (node.kind === 'tool_call' && lastReasoningText) {
      return { ...node, reasoningText: lastReasoningText };
    }

    // Non-reasoning, non-tool-call node resets the trailing reasoning
    if (node.kind !== 'tool_call') {
      lastReasoningText = undefined;
    }

    return node;
  });
}

export function groupParallelNodes(nodes: TimelineNode[]): TimelineNode[] {
  const result: TimelineNode[] = [];
  let i = 0;

  while (i < nodes.length) {
    const current = nodes[i];
    if (!current) {
      i++;
      continue;
    }

    if (current.kind !== 'tool_call' || current.block.startedAt == null) {
      result.push(current);
      i++;
      continue;
    }

    const currentStartedAt = current.block.startedAt;
    const group: ToolCallNode[] = [current];
    let j = i + 1;

    while (j < nodes.length) {
      const next = nodes[j];
      if (
        next?.kind !== 'tool_call' ||
        next.block.startedAt == null ||
        Math.abs(next.block.startedAt - currentStartedAt) > PARALLEL_THRESHOLD_MS
      ) {
        break;
      }
      group.push(next);
      j++;
    }

    if (group.length > 1) {
      const parallelGroup: ParallelGroupNode = {
        kind: 'parallel_group',
        children: group,
      };
      result.push(parallelGroup);
    } else {
      result.push(current);
    }

    i = j;
  }

  return result;
}

function formatDuration(ms: number): string {
  const seconds = Math.round(ms / 1000);
  return seconds === 1 ? '1 second' : `${seconds} seconds`;
}

export function deriveCardState(state: AgentProgress, flatNodes: TimelineNode[]): CardState {
  const toolNodes = flatNodes.filter((n): n is ToolCallNode => n.kind === 'tool_call');
  const taskNodes = flatNodes.filter((n): n is TaskNode => n.kind === 'task');
  const totalSteps = toolNodes.length + taskNodes.length;
  const completedSteps =
    toolNodes.filter((n) => n.block.status === 'completed').length +
    taskNodes.filter((n) => n.block.status === 'completed').length;

  // Determine phase
  let phase: CardPhase;
  if (state.status === 'cancelled') {
    phase = 'cancelled';
  } else if (state.status === 'done') {
    phase = 'complete';
  } else if (state.status === 'error') {
    phase = 'complete';
  } else if (
    toolNodes.some((n) => n.block.status === 'running' || n.block.status === 'completed') ||
    taskNodes.some((n) => n.block.status === 'running' || n.block.status === 'completed')
  ) {
    phase = 'executing';
  } else {
    phase = 'thinking';
  }

  // Derive reasoning summary from the latest reasoning node
  const reasoningNodes = flatNodes.filter((n): n is ReasoningNode => n.kind === 'reasoning');
  const latestReasoning = reasoningNodes[reasoningNodes.length - 1];
  const isCurrentlyReasoning =
    latestReasoning !== undefined && (latestReasoning.streaming || latestReasoning.status === 'running');
  // Only surface the summary when it carries meaningful content (not just "Thinking...")
  const reasoningSummary =
    latestReasoning?.summary && latestReasoning.summary !== 'Thinking...'
      ? latestReasoning.summary
      : undefined;

  // Derive header label and subtitle
  let headerLabel: string;
  let headerSubtitle: string | undefined;
  const stepsLabel = totalSteps > 0 ? `${completedSteps} / ${totalSteps} steps` : undefined;

  switch (phase) {
    case 'thinking': {
      headerLabel = 'Thinking...';
      headerSubtitle = reasoningSummary;
      break;
    }
    case 'executing': {
      if (isCurrentlyReasoning) {
        headerLabel = 'Thinking...';
        headerSubtitle = [stepsLabel, reasoningSummary].filter(Boolean).join(' \u00B7 ') || undefined;
      } else {
        headerLabel = 'Executing...';
        headerSubtitle = [stepsLabel, reasoningSummary].filter(Boolean).join(' \u00B7 ') || undefined;
      }
      break;
    }
    case 'complete': {
      // Sum reasoning durations
      let totalMs = 0;
      for (const n of reasoningNodes) {
        if (n.durationMs !== undefined) totalMs += n.durationMs;
      }
      if (totalSteps > 0) {
        headerLabel = 'Execution Complete';
        headerSubtitle = stepsLabel;
      } else if (totalMs > 0) {
        headerLabel = `Thought for ${formatDuration(totalMs)}`;
      } else {
        headerLabel = 'Thought process';
      }
      break;
    }
    case 'cancelled': {
      headerLabel = 'Paused by user';
      headerSubtitle = stepsLabel;
      break;
    }
  }

  return {
    phase,
    headerLabel,
    headerSubtitle,
    completedSteps,
    totalSteps,
    summaryMetadata: state.summaryMetadata,
    cancelledAt: state.cancelledAt,
  };
}
